import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useNotify } from "../../context/NotifyContext";
import Main from "../Main";
import AppBreadcrumbs from "../../components/ui/AppBreadcrumbs";
import { Button, Typography } from "@mui/material";

import { useFingerprintCapture } from "../../hooks/useFingerprintCapture";
import { getLocation } from "../../components/Navigator/Navigator";
import { aepsRegistration, aepsRegistrationVerify, sendAepsOtpMerchant, verifyAepsOtp } from "../../api/aepsApi";

const OtpVerifyPage = () => {
  const { notify } = useNotify();
  const navigate = useNavigate();
  const { bank: paramBank } = useParams();
  const [searchParams] = useSearchParams();

  const mobileParam = searchParams.get("mobile");
  const { captureFingerprint } = useFingerprintCapture();

  // Bank & mobile
  const [selectedBank, setSelectedBank] = useState(paramBank || sessionStorage.getItem("aepsOtpBank") || "");
  const [mobile, setMobile] = useState(mobileParam || sessionStorage.getItem("aepsOtpMobile") || "");

  const [otp, setOtp] = useState("");
  const [timer, setTimer] = useState(120);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(true);

  // TIMER COUNTDOWN
  useEffect(() => {
    if (timer <= 0) {
      setResendDisabled(false);
      return;
    }
    setResendDisabled(true);
    const interval = setInterval(() => setTimer((t) => t - 1), 1000);
    return () => clearInterval(interval);
  }, [timer]);

  // VERIFY OTP
  const handleVerifyOtp = async () => {
    if (!selectedBank) return notify("Please select a bank", "error");
    if (!mobile) return notify("Mobile number not found", "error");
    if (!otp.trim()) return notify("Enter OTP", "error");

    setLoading(true);
    try {
      if (selectedBank === "bank1") {
        const otpToken = sessionStorage.getItem("aepsOtpToken");
        if (!otpToken) return notify("OTP token missing, please resend OTP", "error");

        const pidData = await captureFingerprint(selectedBank);
        if (!pidData) return notify("Fingerprint capture failed", "error");

        const res = await aepsRegistrationVerify({ otp, otpToken, pidData });
        if (!res.success) return notify(res.message || "OTP verification failed", "error");

        // Clear session
        sessionStorage.removeItem("aepsOtpToken");
        sessionStorage.removeItem("aepsOtpBank");
        sessionStorage.removeItem("aepsOtpMobile");

        notify("AEPS KYC Completed ✅", "success");
      } else {
        const res = await verifyAepsOtp({ otp, pipe: selectedBank });
        if (!res.success) return notify(res.message || "OTP verification failed", "error");

        notify("OTP Verified Successfully ✅", "success");
      }

      navigate("/aeps/dashboard");
    } catch (err) {
      notify(err?.message || "OTP Verification Failed", "error");
    } finally {
      setLoading(false);
    }
  };

  // RESEND OTP
  const handleResend = async () => {
    if (!selectedBank) return notify("Please select a bank to resend OTP", "error");
    if (!mobile) return notify("Mobile number not found", "error");

    setResendLoading(true);
    try {
      if (selectedBank === "bank3") {
        const res = await sendAepsOtpMerchant({ pipe: selectedBank });
        if (!res.success) return notify(res.message || "Failed to send OTP", "error");
        notify(`OTP sent to ${mobile}`, "success");
      } else if (selectedBank === "bank1") {
        const location = await getLocation();
        if (!location) return notify("Location required to resend OTP", "error");

        const res = await aepsRegistration({
          pipe: selectedBank,
          latitude: location.latitude,
          longitude: location.longitude,
        });
        if (!res.success) return notify(res.message || "Failed to send OTP", "error");

        sessionStorage.setItem("aepsOtpToken", res.data?.otpToken);
        notify(`OTP sent to ${mobile}`, "success");
      }

      setTimer(120); // restart timer
    } catch (err) {
      notify(err?.message || "Failed to resend OTP", "error");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <Main>
      <div className="font-[Poppins]">
        <AppBreadcrumbs
          items={[
            { label: "Dashboard", to: "/" },
            { label: "AEPS Dashboard" },
            { label: "Verify OTP" },
          ]}
        />

         <div className="bg-white shadow-md rounded-lg p-4 sm:p-6 w-full max-w-xs lg:max-w-sm mt-4">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4">
            OTP Verification
          </h2>
          <Typography className="text-sm mb-4 text-center opacity-80">
            OTP sent to {mobile || "N/A"}
          </Typography>

          <div className="space-y-4">
            <div>
              <label className="block text-gray-700 font-semibold mb-1">
                Enter OTP <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="Enter OTP"
                className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 outline-none text-sm text-center"
              />
            </div>

            <Typography className="text-center text-sm mb-2">
              Timer: {Math.floor(timer / 60)}:{String(timer % 60).padStart(2, "0")}
            </Typography>

            <div className="flex gap-3 w-full">
              <Button
                onClick={handleResend}
                disabled={resendDisabled || resendLoading}
                fullWidth
                sx={{
                  backgroundColor: "#02026E26",
                  color: "#02026E",
                  fontSize: "12px",
                  fontWeight: 600,
                  height: "40px",
                  borderRadius: "6px",
                  "&:hover": { backgroundColor: "#02026E43" },
                }}
              >
                {resendLoading ? "Resending..." : "Resend OTP"}
              </Button>

              <Button
                onClick={handleVerifyOtp}
                disabled={loading}
                fullWidth
                sx={{
                  backgroundColor: "#02026E26",
                  color: "#02026E",
                  fontSize: "12px",
                  fontWeight: 600,
                  height: "40px",
                  borderRadius: "6px",
                  "&:hover": { backgroundColor: "#02026E43" },
                }}
              >
                {loading ? "Verifying..." : "Verify OTP"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Main>
  );
};

export default OtpVerifyPage;
